import { apiClient } from './api/client'
import type {
  ProcessSubject,
  ProcessSubjectMessage,
  ProcessFollower,
  ProcessListItem,
  ProcessListResponse,
  ProcessListFilter,
  OccurrenceListResponse,
  OccurrenceListStatus,
  OccurrenceListFilter,
  OccurrenceSortKey,
  ProcessSortKey,
  MentionableEmployee,
  StepperResponse,
  ProcessSubjectTopic,
  ProcessSubjectCategory,
  ProcessSubjectStatus,
  ProcessRead,
  ListScopeFilter,
  AnalystSelection,
} from '../features/occurrences/types/occurrences'

/**
 * Anexo já carregado no GCS, pronto para ser referenciado na criação de
 * mensagem ou ocorrência. O cliente obtém `gcsPath` chamando
 * `prepareAttachmentUploads` e fazendo `PUT` direto no `uploadUrl`.
 */
export interface PendingAttachment {
  gcsPath: string
  name: string
  size: number
  type: string
}

/** `company_ids=1,2` — o backend aceita lista separada por vírgula. Vazio =
 * todas as empresas do vínculo (param omitido). */
function companyIdsParam(ids?: number[]): string | undefined {
  return ids && ids.length > 0 ? ids.join(',') : undefined
}

/** Params dos filtros de escopo. Analistas vão como array
 * (`analysts[]=A&analysts[]=B`): nome pode ter vírgula. */
function scopeParams(scope?: ListScopeFilter) {
  const names = (list?: string[]) => (list?.length ? list : undefined)
  return {
    company_ids: companyIdsParam(scope?.companyIds),
    analysts: names(scope?.analysts.client),
    responsible_analysts: names(scope?.analysts.responsible),
  }
}

export const occurrencesService = {
  // ============================ Processes ============================
  async listProcesses(opts: {
    filter?: ProcessListFilter
    search?: string
    scope?: ListScopeFilter
    page?: number
    limit?: number
    sort?: ProcessSortKey
    dir?: 'asc' | 'desc'
  } = {}): Promise<ProcessListResponse> {
    const { scope, ...rest } = opts
    const { data } = await apiClient.get<ProcessListResponse>(
      '/occurrences/processes',
      { params: { ...rest, ...scopeParams(scope) } },
    )
    return data
  },

  /** Opções dos filtros de analista, por tipo (processos em andamento do
   * escopo, nas empresas escolhidas). */
  async listAnalysts(companyIds?: number[]): Promise<AnalystSelection> {
    const { data } = await apiClient.get<AnalystSelection>(
      '/occurrences/processes/analysts',
      { params: { company_ids: companyIdsParam(companyIds) } },
    )
    return data
  },

  /** Visão "por ocorrência": ocorrências de todos os processos do escopo. */
  async listOccurrences(opts: {
    status?: OccurrenceListStatus
    filter?: OccurrenceListFilter
    search?: string
    scope?: ListScopeFilter
    page?: number
    limit?: number
    sort?: OccurrenceSortKey
    dir?: 'asc' | 'desc'
  } = {}): Promise<OccurrenceListResponse> {
    const { scope, ...rest } = opts
    const { data } = await apiClient.get<OccurrenceListResponse>(
      '/occurrences/occurrence-list',
      { params: { ...rest, ...scopeParams(scope) } },
    )
    return data
  },

  /**
   * Booleano agregado de "tenho mensagem não lida em algum processo". Query
   * barata (EXISTS) — alimenta a bolinha vermelha do item Ocorrências na
   * sidebar padrão e o título piscante da aba de Ocorrências.
   */
  async hasUnread(): Promise<boolean> {
    const { data } = await apiClient.get<{ has_unread: boolean }>(
      '/occurrences/processes/unread',
    )
    return data.has_unread
  },

  async getProcessInfo(processId: number): Promise<ProcessListItem | null> {
    const { data } = await apiClient.get<ProcessListItem | null>(
      `/occurrences/processes/${processId}/sidebar-info`,
    )
    return data
  },

  async getStepper(processId: number): Promise<StepperResponse> {
    const { data } = await apiClient.get<StepperResponse>(
      `/occurrences/processes/${processId}/stepper`,
    )
    return data
  },

  async listMentionable(processId: number): Promise<MentionableEmployee[]> {
    const { data } = await apiClient.get<MentionableEmployee[]>(
      `/occurrences/processes/${processId}/mentionable`,
    )
    return data
  },

  // ============================ Subjects ============================
  async listSubjects(
    processId: number,
    topic?: ProcessSubjectTopic,
  ): Promise<ProcessSubject[]> {
    const { data } = await apiClient.get<ProcessSubject[]>(
      `/occurrences/processes/${processId}/subjects`,
      { params: topic ? { topic } : undefined },
    )
    return data
  },

  async getSubject(subjectId: number): Promise<ProcessSubject> {
    const { data } = await apiClient.get<ProcessSubject>(
      `/occurrences/subjects/${subjectId}`,
    )
    return data
  },

  async createSubject(
    processId: number,
    payload: {
      topic: ProcessSubjectCategory
      title: string
      firstMessage: string
    },
  ): Promise<{ subject: ProcessSubject; firstMessage: ProcessSubjectMessage }> {
    const { data } = await apiClient.post(
      `/occurrences/processes/${processId}/subjects`,
      payload,
    )
    return data
  },

  async createOccurrence(
    processId: number,
    payload: {
      title: string
      description: string
      categoria: ProcessSubjectCategory
      responsibleId?: number
      prazo: string
      attachments?: PendingAttachment[]
    },
  ): Promise<{
    subject: ProcessSubject
    firstMessage: ProcessSubjectMessage
    mentions: unknown[]
  }> {
    const { data } = await apiClient.post(
      `/occurrences/processes/${processId}/occurrences`,
      payload,
    )
    return data
  },

  async updateSubjectStatus(
    subjectId: number,
    status: ProcessSubjectStatus,
  ): Promise<ProcessSubject> {
    const { data } = await apiClient.patch<ProcessSubject>(
      `/occurrences/subjects/${subjectId}/status`,
      { status },
    )
    return data
  },

  /**
   * Patch parcial do subject. Hoje só `title`, mas o endpoint backend já
   * está estruturado pra crescer (UpdateSubjectDto).
   */
  async updateSubject(
    subjectId: number,
    patch: { title?: string },
  ): Promise<ProcessSubject> {
    const { data } = await apiClient.patch<ProcessSubject>(
      `/occurrences/subjects/${subjectId}`,
      patch,
    )
    return data
  },

  // ============================ Messages ============================
  async listMessages(subjectId: number): Promise<ProcessSubjectMessage[]> {
    const { data } = await apiClient.get<ProcessSubjectMessage[]>(
      `/occurrences/subjects/${subjectId}/messages`,
    )
    return data
  },

  async createMessage(
    subjectId: number,
    payload: {
      content: string
      attachments?: PendingAttachment[]
      reply_to_message_id?: number | null
    },
  ): Promise<{ message: ProcessSubjectMessage; mentions: unknown[] }> {
    const { data } = await apiClient.post(
      `/occurrences/subjects/${subjectId}/messages`,
      payload,
    )
    return data
  },

  /**
   * Apaga a mensagem (soft delete). O backend preserva conteúdo e anexos na
   * linha para auditoria e passa a devolver a mensagem como apagada.
   */
  async deleteMessage(messageId: number): Promise<ProcessSubjectMessage> {
    const { data } = await apiClient.delete<ProcessSubjectMessage>(
      `/occurrences/subjects/messages/${messageId}`,
    )
    return data
  },

  // ============================ Attachments ============================
  /**
   * Etapa 1 do upload: backend gera signed URLs `PUT` para o cliente enviar
   * os bytes direto ao GCS. Retorna `[{ uploadUrl, gcsPath, name, type, size }]`.
   */
  async prepareAttachmentUploads(
    processId: number,
    files: Array<{ originalName: string; contentType: string; size: number }>,
  ): Promise<
    Array<{
      uploadUrl: string
      gcsPath: string
      name: string
      type: string
      size: number
      /** Headers que DEVEM ser ecoados no PUT — fazem parte da assinatura. */
      uploadHeaders?: Record<string, string>
    }>
  > {
    const { data } = await apiClient.post(
      `/occurrences/processes/${processId}/uploads/prepare`,
      { files },
    )
    return data
  },

  /**
   * Resolve uma signed URL `GET` (5 min) para download de um anexo já
   * persistido. Usado pelo card de anexo no `MessageItem`.
   */
  async getAttachmentDownloadUrl(
    messageId: number,
    index: number,
  ): Promise<string> {
    const { data } = await apiClient.get<{ url: string }>(
      `/occurrences/messages/${messageId}/attachments/${index}/url`,
    )
    return data.url
  },

  // ============================ Followers ============================
  async listFollowers(processId: number): Promise<ProcessFollower[]> {
    const { data } = await apiClient.get<ProcessFollower[]>(
      `/occurrences/processes/${processId}/followers`,
    )
    return data
  },

  async follow(processId: number): Promise<ProcessFollower> {
    const { data } = await apiClient.post<ProcessFollower>(
      `/occurrences/processes/${processId}/follow`,
    )
    return data
  },

  async unfollow(processId: number): Promise<void> {
    await apiClient.delete(`/occurrences/processes/${processId}/follow`)
  },

  // ============================ Reads ============================
  async markRead(
    processId: number,
  ): Promise<{ read: ProcessRead; mentionsMarked: number }> {
    const { data } = await apiClient.post(
      `/occurrences/processes/${processId}/read`,
    )
    return data
  },
}
